<script setup lang="ts">
import type { ITableColumnsOptions } from '@modules/tables/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import { TRANSLATION_GROUP_GENERAL_SETTINGS_PAYMENT_SYSTEMS } from '@modules/available-payment-systems/constants';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import { useI18n } from '@modules/i18n/composables';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';

import {
  ADMIN_GENERAL_SETTINGS_PAYMENT_COUNTRY_GROUPS_UPDATE_ROUTE_NAME,
  TRANSLATION_GROUP_GENERAL_SETTINGS_PAYMENT_COUNTRY_GROUPS,
} from '../../constants';
import { useSettingsPaymentCountryGroupRemoveMutation } from '../../mutations';
import { useSettingsPaymentCountryGroupsListQuery } from '../../queries';

const { confirmDelete } = useConfirmDialogs();
const { getTranslation } = useI18n();

const {
  tableState,
  isLoading,
  data,
  updateTableState,
  refreshTableData,
} = useTableData(useSettingsPaymentCountryGroupsListQuery);

const columnOptions: ITableColumnsOptions = {
  name: {
    defaultMobilePreview: true,
  },
};

const { mutate: deleteItem } = useSettingsPaymentCountryGroupRemoveMutation();

function handleDeleteAction(event: Event, id: number): void {
  confirmDelete({
    event,
    header: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PAYMENT_SYSTEMS, 'REMOVE_MODAL_TITLE').value,
    message: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PAYMENT_SYSTEMS, 'REMOVE_MODAL_DELETE_QUESTION').value,
    acceptLabel: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PAYMENT_SYSTEMS, 'REMOVE_MODAL_ANSWER_YES').value,
    rejectLabel: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PAYMENT_SYSTEMS, 'REMOVE_MODAL_ANSWER_NO').value,
    accept: () => deleteItem({ id }),
  });
}
</script>

<template>
  <VCard v-data-qa-auto-id="'PaymentCountryGroupsList'"
    type="table"
  >
    <VTable
      :data="data"
      :state="tableState"
      :loading="isLoading"
      :title-t-path="$getTPath(TRANSLATION_GROUP_GENERAL_SETTINGS_PAYMENT_COUNTRY_GROUPS, 'HEADER')"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @refresh="refreshTableData"
    >
      <template #recordAction="{ listItem }">
        <VIconButton
          v-if="!listItem.default"
          icon="pi-file-edit"
          :to="{ name: ADMIN_GENERAL_SETTINGS_PAYMENT_COUNTRY_GROUPS_UPDATE_ROUTE_NAME, params: { id: listItem.id } }"
        />
        <VIconButton
          v-if="!listItem.default"
          mode="link"
          icon="pi-trash"
          @click="handleDeleteAction($event, listItem.id)"
        />
      </template>
      <template #footerButtons>
        <VButton
          :to="{ name: ADMIN_GENERAL_SETTINGS_PAYMENT_COUNTRY_GROUPS_UPDATE_ROUTE_NAME }"
          :label="$getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_PAYMENT_SYSTEMS, 'ADD_NEW_COUNTRY_GROUP')"
        />
      </template>
    </VTable>
  </VCard>
</template>
